import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'formatafacil-breadcrumb-visible';

function read(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

/**
 * Hook para controlar a visibilidade do breadcrumb flutuante.
 * Oculto por padrão; o usuario pode ativar nas configurações de aparência.
 * Persiste no Supabase e no localStorage como fallback.
 */
export function useBreadcrumbVisible() {
  const { user } = useAuth();
  const [visible, set_visible] = useState(read);

  /** Carrega do Supabase ao montar */
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('breadcrumb_visible')
        .eq('id', user.id)
        .single();

      if (!cancelled && data && typeof (data as Record<string, unknown>).breadcrumb_visible === 'boolean') {
        const val = (data as Record<string, unknown>).breadcrumb_visible as boolean;
        set_visible(val);
        try { localStorage.setItem(STORAGE_KEY, val ? '1' : '0'); } catch {}
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  /** Sincroniza entre abas */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) set_visible(read());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggle = useCallback(async (next: boolean) => {
    set_visible(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch {}
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));

    if (user?.id) {
      await supabase
        .from('profiles')
        .update({ breadcrumb_visible: next } as Record<string, unknown>)
        .eq('id', user.id);
    }
  }, [user?.id]);

  return { breadcrumb_visible: visible, toggle_breadcrumb: toggle };
}
